/* ──────────────────────────────────────────────────────────────────────────
 * Public "Global Reseller Program" marketing page — reuses the `.lpv3` design
 * system (LandingPageV3.css) so it is visually 1:1 with the landing page.
 * Copy is ported from the in-app Global Reseller Program screen
 * (DashboardNew.jsx → Reseller component). Linked from the Pricing page banner
 * and the nav "Reseller Program" link.
 * Tabler icons (`ti ti-*`) + Playfair/DM Sans loaded in public/index.html.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import ContactLeadDialog from "@/components/ContactLeadDialog";
import CookieConsent from "@/components/CookieConsent";
import "./LandingPageV3.css";

const NAV_LINKS = [
  { label: "Advisors", path: "/advisors" },
  { label: "Resources", path: "/resources" },
  { label: "IPO FAQ's", path: "/ipo-faq" },
  { label: "Pricing", path: "/pricing" },
  { label: "About Us", path: "/about" },
  { label: "Careers", path: "/careers" },
  { label: "Reseller Program", path: "/reseller-network" },
];

const VALUE_PROPS = [
  {
    icon: "ti-coins",
    h: "25% on every deal",
    d: "Earn a 25% revenue share on every mandate you bring in — across advisory, funding and platform fees.",
  },
  {
    icon: "ti-refresh",
    h: "Recurring, not one-off",
    d: "You earn as revenue is recognised through the entire mandate lifecycle — not a one-time finder's fee.",
  },
  {
    icon: "ti-layout-dashboard",
    h: "Full partner enablement",
    d: "A partner dashboard with referral tracking, attribution, payout statements and co-branded collateral — built for SME & MSME mandates Pan-India.",
  },
];

const AUDIENCE = [
  {
    icon: "ti-building-store",
    h: "E-commerce platforms",
    step: "Enterprise · Large resellers · ₹5 Cr+ profit",
    d: "Marketplaces and aggregators with deep merchant networks — surface IPO-readiness to your top-performing sellers as a value-add.",
  },
  {
    icon: "ti-calculator",
    h: "CA & CS firms",
    step: "Channel · Practices with SME client books",
    d: "Your clients already trust you with their books. Refer the IPO-ready ones and earn on advisory, DRHP and funding revenue.",
  },
  {
    icon: "ti-user-dollar",
    h: "Independent / fractional CFOs",
    step: "Individual · Part-time finance leaders",
    d: "Sitting across multiple MSME boards, you see who's ready before anyone else. Originate mandates and earn on each.",
  },
  {
    icon: "ti-building-bank",
    h: "Banks & NBFCs",
    step: "Institutional · Relationship-led institutions",
    d: "Channel your MSME lending relationships into IPO mandates and co-earn across the funding and advisory stack.",
  },
];

const STEPS = [
  { icon: "ti-send", h: "Refer", step: "Step 1", d: "Submit an MSME via your partner dashboard or unique link." },
  { icon: "ti-clipboard-check", h: "Onboard", step: "Step 2", d: "We run readiness & KYC; you stay looped in throughout." },
  { icon: "ti-rocket", h: "Engage", step: "Step 3", d: "The MSME activates advisory, DRHP or funding on the platform." },
  { icon: "ti-cash", h: "Earn", step: "Step 4", d: "Your 25% share is paid as revenue is recognised." },
];

const ECONOMICS = [
  { icon: "ti-building-bank", v: "₹3–8 L", l: "Avg platform revenue / SME mandate" },
  { icon: "ti-coin", v: "₹75K–2 L", l: "Your 25% share per deal" },
  { icon: "ti-refresh", v: "Recurring", l: "Across mandate lifecycle, not one-off" },
];

const ResellerNetworkPage = () => {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const go = (path) => navigate(path);

  // Browser Back on this page returns the user to their Home Page
  // (kept logged in — client-side SPA navigation, no session change).
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => navigate("/home");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [navigate]);

  return (
    <div className="lpv3" data-testid="reseller-network-page">
      <Seo
        title="India Reseller & Partner Program | IPOLabs"
        description="Join the IPOLabs India Reseller Program — earn recurring revenue by bringing IPO-bound companies to India's unified IPO platform. Built for CA/CS firms & CFOs."
        path="/reseller-network"
      />
      <h2 className="sr-only">
        IPOLabs Global Reseller Program — a Pan-India referral program paying 25% revenue share per deal,
        with partner tiers for large e-commerce resellers, CA & CS firms, independent CFOs and banks,
        focused on SME and MSME IPO mandates.
      </h2>

      {/* HEADER */}
      <div className="hd" data-testid="reseller-header">
        <div className="hd-inner">
          <img src="/ipolabs-logo.png" alt="IPOLabs" className="brand-logo" onClick={() => go("/")} data-testid="reseller-logo-btn" />
          <div className="nav">
            {NAV_LINKS.map((l) => (
              <a key={l.path} onClick={() => go(l.path)} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>{l.label}</a>
            ))}
          </div>
          <div className="hd-cta">
            <span className="hd-login" onClick={() => go("/login")} data-testid="reseller-login-link">Login</span>
            <button className="btn-purple" onClick={() => go("/login")} data-testid="reseller-get-started-btn">Get Started →</button>
          </div>
        </div>
      </div>

      {/* HERO */}
      <div className="hero" data-testid="reseller-hero">
        <span className="hero-pill"><i className="ti ti-world-share" aria-hidden="true" /> Global Reseller Program · 25% revenue share</span>
        <div className="hero-h">Refer an SME to IPO. Earn 25% on every deal — Pan-India.</div>
        <div className="hero-sub">
          A partner network for those already trusted by India&apos;s businesses — large resellers, CA firms,
          fractional CFOs and banks. Bring MSMEs into their IPO journey and share in the revenue of every
          mandate you originate.
        </div>
        <div className="hero-btns">
          <button className="btn-dark" onClick={() => setContactOpen(true)} data-testid="reseller-apply-btn"><i className="ti ti-rocket" aria-hidden="true" /> Become a Partner</button>
          <button className="btn-line" onClick={() => go("/pricing")} data-testid="reseller-pricing-btn">View pricing</button>
        </div>
        <div className="hero-stats">
          <div><div className="hs-v">25%</div><div className="hs-l">revenue share / deal</div></div>
          <div><div className="hs-v">Recurring</div><div className="hs-l">through mandate lifecycle</div></div>
          <div><div className="hs-v">Pan-India</div><div className="hs-l">SME & MSME mandates</div></div>
          <div><div className="hs-v">₹0</div><div className="hs-l">to join</div></div>
        </div>
      </div>

      {/* WHY PARTNER */}
      <div className="sec" data-testid="reseller-why">
        <div className="sec-kick">Why partner with us</div>
        <div className="sec-h">A partnership that <span className="accent">pays you back</span></div>
        <div className="sec-d">More than a referral scheme — a true revenue partnership backed by India&apos;s most transparent IPO platform.</div>
        <div className="vals">
          {VALUE_PROPS.map((v, i) => (
            <div className="val" key={i} data-testid={`reseller-value-${i}`}>
              <div className="val-ic"><i className={`ti ${v.icon}`} aria-hidden="true" /></div>
              <div className="val-h">{v.h}</div>
              <div className="val-d">{v.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* WHO CAN BECOME A PARTNER */}
      <div className="sec alt" data-testid="reseller-audience">
        <div className="sec-kick">Partner tiers</div>
        <div className="sec-h">Who can become a partner</div>
        <div className="sec-d">If India&apos;s businesses already trust you with their books, capital or platform, the program turns that trust into recurring income.</div>
        <div className="mods">
          {AUDIENCE.map((m, i) => (
            <div className="mod" key={i} data-testid={`reseller-audience-${i}`}>
              <div className="mod-top">
                <div className="mod-ic"><i className={`ti ${m.icon}`} aria-hidden="true" /></div>
                <div><div className="mod-h">{m.h}</div><div className="mod-step">{m.step}</div></div>
              </div>
              <div className="mod-d">{m.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* HOW THE REFERRAL FLOW WORKS */}
      <div className="sec" data-testid="reseller-how">
        <div className="sec-kick">How it works</div>
        <div className="sec-h">How the referral flow works</div>
        <div className="sec-d">No upfront cost, no lock-in. Refer, onboard, engage and earn — we handle tracking, billing and payouts.</div>
        <div className="mods">
          {STEPS.map((m, i) => (
            <div className="mod" key={i} data-testid={`reseller-step-${i}`}>
              <div className="mod-top">
                <div className="mod-ic"><i className={`ti ${m.icon}`} aria-hidden="true" /></div>
                <div><div className="mod-h">{m.h}</div><div className="mod-step">{m.step}</div></div>
              </div>
              <div className="mod-d">{m.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ILLUSTRATIVE PARTNER ECONOMICS */}
      <div className="sec alt" data-testid="reseller-economics">
        <div className="sec-kick">Partner economics</div>
        <div className="sec-h">Illustrative partner economics</div>
        <div className="sec-d">A snapshot of what a single SME mandate can mean for you — recurring, not one-off.</div>
        <div className="vals">
          {ECONOMICS.map((e, i) => (
            <div className="val" key={i} data-testid={`reseller-econ-${i}`} style={{ textAlign: "center" }}>
              <div className="val-ic" style={{ margin: "0 auto 12px" }}><i className={`ti ${e.icon}`} aria-hidden="true" /></div>
              <div className="hs-v" style={{ fontSize: 30 }}>{e.v}</div>
              <div className="val-d" style={{ marginTop: 6 }}>{e.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="cta" data-testid="reseller-cta">
        <div className="cta-h">Ready to grow with IPOLabs?</div>
        <div className="cta-d">Partners get a dashboard with referral tracking, attribution, payout statements and co-branded collateral. Focused on SME &amp; MSME IPO mandates Pan-India.</div>
        <div className="hero-btns">
          <button className="btn-dark" onClick={() => setContactOpen(true)} data-testid="reseller-cta-apply-btn"><i className="ti ti-rocket" aria-hidden="true" /> Become a Partner</button>
          <button className="btn-line" onClick={() => setContactOpen(true)} data-testid="reseller-cta-talk-btn">See full terms</button>
        </div>
      </div>

      {/* FOOTER */}
      <div className="ft">
        <div className="ft-grid">
          <div>
            <div className="ft-brand">IPO<span>Labs</span></div>
            <div className="ft-tag">India&apos;s end-to-end IPO management platform — helping founders, promoters and CFOs go public with clarity and control.</div>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Platform</div>
            <a onClick={() => go("/login")}>IPO Journey</a>
            <a onClick={() => go("/login?module=matchmaker")}>Advisor Marketplace</a>
            <a onClick={() => go("/login?module=drhp")}>DRHP Workspace</a>
            <a onClick={() => go("/login?module=funding")}>Funding Desk</a>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Company</div>
            <a onClick={() => go("/about")}>About Us</a>
            <a onClick={() => go("/pricing")}>Pricing</a>
            <a onClick={() => go("/careers")}>Careers</a>
            <a onClick={() => go("/reseller-network")}>Reseller Program</a>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Get started</div>
            <a onClick={() => go("/login")}>Log in</a>
            <a onClick={() => go("/login")}>Create workspace</a>
            <a onClick={() => go("/advisor-marketplace")}>Join as advisor</a>
            <a onClick={() => setContactOpen(true)}>Become a partner</a>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Legal &amp; compliance</div>
            <a onClick={() => go("/disclaimer")}>Disclaimer</a>
            <a onClick={() => go("/resources")}>Resources</a>
            <a onClick={() => go("/disclaimer")}>Privacy Policy</a>
            <a onClick={() => go("/disclaimer")}>Terms of Services</a>
          </div>
        </div>
        <div className="ft-bot">
          <span>© {new Date().getFullYear()} IPOLabs. All rights reserved.</span>
          <span>IPOLabs · India&apos;s end-to-end IPO management platform</span>
        </div>
      </div>

      <ContactLeadDialog open={contactOpen} onOpenChange={setContactOpen} leadType="sales" />
      <CookieConsent />
    </div>
  );
};

export default ResellerNetworkPage;
